import { useCallback, useEffect, useRef, useState } from 'react';
import type { FuseSortFunctionArg } from 'fuse.js';

import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import {
	getAllElementInsertionStats,
	incrementElementCount,
} from '../utils/elementInsertionsStats';

export const getMacroToPriorityMap = (): Map<string, number> => {
	const map = new Map<string, number>();

	const elementUsageStats = getAllElementInsertionStats();

	for (const elementUsage of elementUsageStats) {
		const priority = 1 / elementUsage.count;
		map.set(elementUsage.element, priority);
	}

	return map;
};

export const useSmartElementRecommendations = () => {
	const [forceRebuildMap, setForceRebuildMap] = useState<boolean>(false);
	const macroToPriorityRef = useRef(new Map<string, number>());

	useEffect(() => {
		const map = getMacroToPriorityMap();
		macroToPriorityRef.current = map;
	}, [forceRebuildMap]);

	const prioritySortingFn = useCallback(
		(items: QuickInsertItem[]) =>
			(a: FuseSortFunctionArg, b: FuseSortFunctionArg): number => {
				const aTitle = items[a.idx]?.title;
				const bTitle = items[b.idx]?.title;

				const aPriority = macroToPriorityRef.current.get(aTitle) ?? 1;
				const bPriority = macroToPriorityRef.current.get(bTitle) ?? 1;

				const adjustedScoreA = (a.score ?? 1) * aPriority;
				const adjustedScoreB = (b.score ?? 1) * bPriority;

				return adjustedScoreA - adjustedScoreB;
			},
		[],
	);

	const handleInsert = useCallback((item: QuickInsertItem) => {
		if (incrementElementCount(item)) {
			setForceRebuildMap((prev) => !prev);
		}
	}, []);

	const shouldSortElements = expValEquals(
		'confluence_frontend_smart_element_recommendations',
		'cohort',
		'variant1',
	);
	const shouldCaptureElementInsertionStats = fg('confluence_frontend_track_element_analytics');

	return {
		prioritySortingFn: shouldSortElements ? prioritySortingFn : undefined,
		handleInsert: shouldCaptureElementInsertionStats ? handleInsert : undefined,
	};
};
